// @ts-strict-ignore
import { extractThresholdIds } from 'flow/service/thresholds';
import { extractDataRouteIds } from 'flow/service/dataRoutes';
import { ActionType, DependencyType } from '../constants';
import type {
  SchemaInstance,
} from '../types';
import { isActionOfType } from '../../../service/actions';


/**
 * add the action threshold dependencies to the `dependencies` map passed in
 */
function appendActionThresholdDependencies({
  action,
  dependencies,
  thresholds,
}: {
  action: SchemaInstance['actions'][string];
  dependencies: object;
  thresholds: SchemaInstance['thresholds'];
}) {
  const thresholdIdsForAction = extractThresholdIds(action.rules);

  thresholdIdsForAction.forEach(thresholdId => {
    const actionPublicId = action.publicId;

    const threshold = thresholds[thresholdId];
    const leftSideThresholdActionId = threshold.left.actionId;
    const rightSideThresholdActionId = threshold.right.actionId;

    /**
     * some existing older workflows saved from the legacy editor have cyclical thresholds in them,
     * where a threshold references the action that owns it (ie. "if tag A is set, set tag A").
     * we want to avoid building a dependency for those cyclical cases as they are incorrect and
     * interfere with the rest of the application.
     */
    if (threshold.actionId === leftSideThresholdActionId) {
      return;
    }

    if (!(Object.hasOwn(dependencies, actionPublicId))) {
      dependencies[actionPublicId] = [];
    }

    if (leftSideThresholdActionId) {
      dependencies[actionPublicId].push({
        id: leftSideThresholdActionId,
        isPreview: !!threshold?.isPreview,
        type: DependencyType.LEFT_SIDE_CONDITION,
      });
    }

    if (rightSideThresholdActionId) {
      dependencies[actionPublicId].push({
        id: rightSideThresholdActionId,
        type: DependencyType.RIGHT_SIDE_CONDITION,
      });
    }
  });
}

/**
 * add the data route source dependencies to the `dependencies` map passed in
 */
function appendActionDataRouteSourceDependencies({
  action,
  dataRoutes,
  dependencies,
}: {
  action: SchemaInstance['actions'][string];
  dataRoutes: SchemaInstance['dataRoutes'];
  dependencies: SchemaInstance['dependencies'];
}) {
  const dataRouteSourceIds = isActionOfType(action, ActionType.OUTPUT)
    ? extractDataRouteIds(action.output.expression)
    : Object.values(dataRoutes).filter(dataRoute => (
      dataRoute.targetId === action.publicId
      && (dataRoute.category.category_id !== null || Boolean(dataRoute.target_payload_key))
    )).map(dataRoute => dataRoute.publicId);

  if (dataRouteSourceIds.length) {
    dataRouteSourceIds.forEach(dataRouteSourceId => {
      const actionPublicId = action.publicId;
      if (!(actionPublicId in dependencies)) {
        dependencies[actionPublicId] = [];
      }

      const dataRoute = dataRoutes[dataRouteSourceId];
      dependencies[actionPublicId].push({
        id: dataRoute.sourceId,
        type: DependencyType.DATA_ROUTE_SOURCE,
      });
    });
  }
}

/**
 * build an object containing the indexed relationships between actions
 */
export function buildDependencies(workflowState: Pick<SchemaInstance, 'actions' | 'thresholds' | 'dataRoutes'>) {
  const { actions, thresholds, dataRoutes } = workflowState;
  const dependencies: SchemaInstance['dependencies'] = {};

  Object.values(actions).forEach(action => {
    if (action.rules) {
      appendActionThresholdDependencies({
        action,
        dependencies,
        thresholds,
      });
    }

    if (
      action.service
      || action.output?.expression
      /** Even if matrix model has no thresholds,
      * we want to honor the dependencies based on linked axes */
      || action.type === ActionType.MATRIX_MODEL
    ) {
      appendActionDataRouteSourceDependencies({
        action,
        dataRoutes,
        dependencies,
      });
    }
  });

  return dependencies;
}

